import { useEffect, useState } from 'react'
import { useTranslation } from '@kostbar/i18n'
import type {
  VivaConfig,
  VivaConfigRequest,
  VivaMode,
} from '@/api/endpoints/viva-config'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface Props {
  config: VivaConfig | null | undefined
  onSave: (body: VivaConfigRequest) => void
  onRemove: () => void
  saving?: boolean
  removing?: boolean
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const API_ORIGIN = import.meta.env.VITE_API_BASE_URL || window.location.origin

/**
 * Per-restaurant Viva configuration (CHG-2026-08-viva-isv, CHG-2026-08-viva-direct-merchant).
 * No config row means the restaurant cannot accept card payments. DIRECT mode runs payments
 * through the restaurant's own Viva merchant account (full credential set); ISV mode runs them
 * through webbill's ISV partner scheme with a fixed fee per payment.
 */
export function RestaurantVivaForm({
  config,
  onSave,
  onRemove,
  saving,
  removing,
}: Props) {
  const { t } = useTranslation('restaurants')
  const [mode, setMode] = useState<VivaMode>('DIRECT')
  const [merchantId, setMerchantId] = useState('')
  const [feeCents, setFeeCents] = useState('0')
  const [sourceCode, setSourceCode] = useState('')
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [apiKey, setApiKey] = useState('')

  useEffect(() => {
    setMode(config?.mode ?? 'DIRECT')
    setMerchantId(config?.merchantId ?? '')
    setFeeCents(String(config?.isvFeeCents ?? 0))
    setSourceCode(config?.sourceCode ?? '')
    setClientId(config?.clientId ?? '')
    setClientSecret(config?.clientSecret ?? '')
    setApiKey(config?.apiKey ?? '')
  }, [config])

  const merchantIdValid = UUID_PATTERN.test(merchantId.trim())
  const sourceCodeValid = sourceCode.trim() !== ''
  const fee = Number.parseInt(feeCents, 10)
  const feeValid = Number.isInteger(fee) && fee >= 0
  const credentialsValid =
    clientId.trim() !== '' && clientSecret.trim() !== '' && apiKey.trim() !== ''
  const canSave =
    merchantIdValid &&
    sourceCodeValid &&
    (mode === 'DIRECT' ? credentialsValid : feeValid)

  const submit = () => {
    if (!canSave) return
    // Changing the merchant account strands refunds of payments created on the previous
    // account once no restaurant carries it anymore — make that an explicit decision.
    if (
      config &&
      config.merchantId !== merchantId.trim() &&
      !window.confirm(t('viva.merchantChangeWarning'))
    ) {
      return
    }
    const body: VivaConfigRequest = {
      mode,
      merchantId: merchantId.trim(),
      sourceCode: sourceCode.trim(),
    }
    if (mode === 'DIRECT') {
      body.clientId = clientId.trim()
      body.clientSecret = clientSecret.trim()
      body.apiKey = apiKey.trim()
    } else {
      body.isvFeeCents = fee
    }
    onSave(body)
  }

  const webhookUrl = config?.webhookPath
    ? `${API_ORIGIN}${config.webhookPath}`
    : null

  return (
    <div className="max-w-xl space-y-6">
      <p className="text-muted-foreground text-sm">
        {config ? t('viva.activeHint') : t('viva.inactiveHint')}
      </p>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">{t('viva.mode')}</legend>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="viva-mode"
              value="DIRECT"
              checked={mode === 'DIRECT'}
              onChange={() => setMode('DIRECT')}
            />
            {t('viva.modeDirect')}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="viva-mode"
              value="ISV"
              checked={mode === 'ISV'}
              onChange={() => setMode('ISV')}
            />
            {t('viva.modeIsv')}
          </label>
        </div>
        <p className="text-muted-foreground text-sm">
          {mode === 'DIRECT' ? t('viva.modeDirectHint') : t('viva.modeIsvHint')}
        </p>
      </fieldset>

      <div className="space-y-2">
        <Label htmlFor="viva-merchant-id">{t('viva.merchantId')}</Label>
        <Input
          id="viva-merchant-id"
          value={merchantId}
          onChange={(e) => setMerchantId(e.target.value)}
          placeholder={t('viva.merchantIdPlaceholder')}
        />
        {merchantId.trim() !== '' && !merchantIdValid && (
          <p className="text-destructive text-sm">
            {t('viva.merchantIdInvalid')}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="viva-source-code">{t('viva.sourceCode')}</Label>
        <Input
          id="viva-source-code"
          value={sourceCode}
          onChange={(e) => setSourceCode(e.target.value)}
          placeholder={t('viva.sourceCodePlaceholder')}
          className="w-40"
        />
        <p className="text-muted-foreground text-sm">
          {t('viva.sourceCodeHint')}
        </p>
      </div>

      {mode === 'DIRECT' && (
        <>
          <div className="space-y-2">
            <Label htmlFor="viva-client-id">{t('viva.clientId')}</Label>
            <Input
              id="viva-client-id"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="viva-client-secret">{t('viva.clientSecret')}</Label>
            <Input
              id="viva-client-secret"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="viva-api-key">{t('viva.apiKey')}</Label>
            <Input
              id="viva-api-key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
            <p className="text-muted-foreground text-sm">
              {t('viva.credentialsHint')}
            </p>
          </div>
        </>
      )}

      {mode === 'ISV' && (
        <div className="space-y-2">
          <Label htmlFor="viva-fee-cents">{t('viva.feeCents')}</Label>
          <Input
            id="viva-fee-cents"
            value={feeCents}
            onChange={(e) => setFeeCents(e.target.value)}
            type="number"
            min={0}
            step={1}
            className="w-40"
          />
          <p className="text-muted-foreground text-sm">
            {t('viva.feeCentsHint')}
          </p>
        </div>
      )}

      {webhookUrl && (
        <div className="space-y-2">
          <Label htmlFor="viva-webhook-url">{t('viva.webhookUrl')}</Label>
          <div className="flex items-center gap-2">
            <Input
              id="viva-webhook-url"
              value={webhookUrl}
              readOnly
              className="font-mono text-xs"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => void navigator.clipboard.writeText(webhookUrl)}
            >
              {t('viva.copy')}
            </Button>
          </div>
          <p className="text-muted-foreground text-sm">
            {t('viva.webhookUrlHint')}
          </p>
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button onClick={submit} disabled={!canSave || saving}>
          {saving ? t('viva.saving') : t('viva.save')}
        </Button>
        {config && (
          <Button variant="destructive" onClick={onRemove} disabled={removing}>
            {removing ? t('viva.removing') : t('viva.remove')}
          </Button>
        )}
      </div>
    </div>
  )
}
